/**
 * driver.ts — the customer's own record, read and written where the canvas edits it.
 *
 * THE WRITES ARE `DriverArea`'S SHEET MUTATIONS, MOVED WITHOUT A CHARACTER CHANGED IN WHAT THEY
 * TOUCH: the same `profiles` bucket and `photos/<uuid>.<ext>` path, the same
 * `driver-identification` bucket with its `documents/licences` and `documents/national-ids`
 * subfolders, the same public URLs. Storage paths are not an implementation detail — every
 * document already on file lives at one, and a "tidier" path is a file nobody can find.
 *
 * WHAT DID CHANGE IS THE BLAST RADIUS. The sheet held every profile column in `useState` and sent
 * all eight on every save, so committing a corrected phone number also rewrote the name, the
 * address and the date of birth from whatever the form happened to be holding — which is a
 * background refetch away from writing stale values back over fresh ones. Each block now sends
 * only the columns it owns, which is what `patch` is.
 */

import { supabase } from '@/integrations/supabase/client';

import type { DriverDocument } from './driverDocs';

export const contractDriverKey = (contractId: number) => ['contract-driver', contractId] as const;

/**
 * The same key and the same select the driver surface has always used — plus `profiles.admin_id`.
 *
 * THE EMBED CAN CARRY TWO ROWS AND ONLY ONE OF THEM IS THE DRIVER'S (see `driverProfileOf`). It
 * matters more here than anywhere else on the page: this read supplies `profileId` to
 * `saveDriverProfile`, so picking by position did not merely DISPLAY the wrong person — the next
 * inline save would have written the driver's name, phone and date of birth over an ADMIN's own
 * profile row.
 */
export async function fetchContractDriver(contractId: number) {
    const { data, error } = await supabase
        .from('contracts')
        .select(`
            id,
            driver_id,
            drivers(
                id,
                repayment_tag,
                profiles(
                    id, admin_id, first_name, middle_name, last_name, birth_date,
                    phone, email, address, photo
                ),
                driver_identification(id, type, document_url, expires_at)
            )
        `)
        .eq('id', contractId)
        .single();
    if (error) throw error;
    return data;
}

/** The columns a block may own. Anything absent is left exactly as the server holds it. */
export interface DriverProfilePatch {
    first_name?: string;
    middle_name?: string | null;
    last_name?: string;
    birth_date?: string | null;
    phone?: string;
    email?: string | null;
    address?: string | null;
    photo?: string | null;
}

/**
 * Save one block's worth of profile columns, uploading a staged photograph first.
 *
 * THE PHOTO UPLOAD RUNS BEFORE THE UPDATE and its failure stops it, which is the order the sheet
 * used: a profile row pointing at a URL that never landed shows a broken image with no way to tell
 * whether the file or the record is wrong.
 *
 * NO PROFILE ID IS A REFUSAL, NOT A NO-OP. `DriverArea` passes `profile?.id ?? 0`, so a driver with
 * no profile row of their own — the whole `profiles` embed belonging to an admin, or empty — used
 * to send `WHERE id = 0`: PostgREST matches nothing, returns no error, and the canvas reported a
 * successful save of a value that was never written. Saying so is the difference between a bug an
 * admin can see and one they find out about when the agreement prints blank.
 */
export async function saveDriverProfile({
    profileId,
    patch,
    photoFile,
}: {
    profileId: number;
    patch: DriverProfilePatch;
    photoFile?: File | null;
}): Promise<void> {
    if (!profileId) {
        throw new Error('This driver has no profile record yet. Create the driver’s profile first.');
    }

    const body: DriverProfilePatch = { ...patch };

    if (photoFile) {
        const fileExt = photoFile.name.split('.').pop();
        const fileName = `photos/${crypto.randomUUID()}.${fileExt}`;
        const { error: uploadError } = await supabase.storage
            .from('profiles')
            .upload(fileName, photoFile);
        if (uploadError) throw uploadError;
        body.photo = supabase.storage.from('profiles').getPublicUrl(fileName).data.publicUrl;
    }

    if (Object.keys(body).length === 0) return;

    const { error } = await supabase.from('profiles').update(body).eq('id', profileId);
    if (error) throw error;
}

/** `drivers.repayment_tag` — the one field on the driver row rather than the profile. */
export async function saveRepaymentTag({
    driverId,
    repaymentTag,
}: {
    driverId: number;
    repaymentTag: string;
}): Promise<void> {
    const { error } = await supabase
        .from('drivers')
        .update({ repayment_tag: repaymentTag.trim() || null })
        .eq('id', driverId);
    if (error) throw error;
}

/**
 * Create or replace one identity document.
 *
 * A DOCUMENT ROW WITHOUT A DOCUMENT CANNOT EXIST — `driver_identification.document_url` is NOT
 * NULL — so a first save with no file is refused here, in words, rather than by a constraint. An
 * amendment with no new file keeps the URL it has, which is how the expiry alone gets corrected.
 */
export async function saveDriverDocument({
    driverId,
    type,
    existing,
    file,
    expiresAt,
}: {
    driverId: number;
    type: string;
    existing: DriverDocument | undefined;
    file: File | null;
    expiresAt: string;
}): Promise<void> {
    let documentUrl: string;

    if (file) {
        const fileExt = file.name.split('.').pop();
        const subfolder =
            type === 'DRIVING_LICENCE' ? 'documents/licences' : 'documents/national-ids';
        const fileName = `${subfolder}/${crypto.randomUUID()}.${fileExt}`;
        const { error: uploadError } = await supabase.storage
            .from('driver-identification')
            .upload(fileName, file);
        if (uploadError) throw uploadError;
        documentUrl = supabase.storage.from('driver-identification').getPublicUrl(fileName).data
            .publicUrl;
    } else {
        if (!existing?.document_url) throw new Error('Choose the document file first.');
        documentUrl = existing.document_url;
    }

    if (existing) {
        const { error } = await supabase
            .from('driver_identification')
            .update({ document_url: documentUrl, expires_at: expiresAt || null })
            .eq('id', existing.id);
        if (error) throw error;
        return;
    }

    // `type` is a narrowed enum in the generated types and the caller only ever passes one of the
    // two the driver file draws. This is the cast this codebase already uses for enum columns.
    const { error } = await supabase.from('driver_identification').insert({
        driver_id: driverId,
        type: type as 'DRIVING_LICENCE' | 'NATIONAL_ID',
        document_url: documentUrl,
        expires_at: expiresAt || null,
    });
    if (error) throw error;
}
